import React, { useEffect, useState } from 'react';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { TableRowsOutlined, ViewTimelineOutlined } from '@mui/icons-material';
import ReductionActionService from '../../../shared/services/reduction-action/reduction-action.service';
import TargetSettingService from '../../../shared/services/target-setting/target-setting.service';
import { ReductionActionPlan } from '../../presentation-components/reduction-components/reduction-action-plan';
import { ReductionActionTimeline } from '../../presentation-components/reduction-components/reduction-action-timeline';
import { IReductionActionForReportPeriod, ReductionActionType } from '../../../shared/services/reduction-action/reduction-action.model';
import { LoadingPage } from '../loading-page';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { useNavigate } from 'react-router-dom';
import { useIsReportPeriodLocked } from '../../../hooks/use-is-report-period-locked';
import { InformationBox } from '../../presentation-components/miscellaneous-components/information-box';

type ReductionActionView = 'table' | 'timeline';

export const ReductionActionPlanPage = ({
  actionType = 'reduction',
  pageTitle = 'Reduction Actions',
  addButtonText = 'Add Reduction Action',
  addButtonLink = '/reduction-plan/actions/library',
  emptyInfoText,
}: {
  actionType?: ReductionActionType;
  pageTitle?: string;
  addButtonText?: string;
  addButtonLink?: string;
  emptyInfoText?: string;
}) => {
  const navigate = useNavigate()
  //Hooks
  const isReportPeriodLocked = useIsReportPeriodLocked()
  //Services
  const reductionActionService = new ReductionActionService()
  const targetSettingService = new TargetSettingService()
  //State
  const [organisationReductionActions, setOrganisationReductionActions] = useState<IReductionActionForReportPeriod[]>()
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //View toggle (table vs timeline)
  const [view, setView] = useState<ReductionActionView>('table')
  const [timelineActions, setTimelineActions] = useState<IReductionActionForReportPeriod[]>()
  const [isTimelineLoading, setIsTimelineLoading] = useState<boolean>(false)
  const [baseYear, setBaseYear] = useState<number | null>(null)

  useEffect(() => {
    targetSettingService.getTargetSettings()
      .then((response) => {
        const row = Array.isArray(response) ? response[0] : response
        const base = row?.baseYear != null ? Number(row.baseYear) : null
        setBaseYear(base != null && !Number.isNaN(base) ? base : null)
      })
      .catch((err) => console.log(err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getAllActionsForOrganisation();
  }, [inputPage, perPage, order, orderBy])

  // Timeline needs every action (not just the current page), refreshed each time it's shown.
  const loadTimelineActions = () => {
    setIsTimelineLoading(true)
    reductionActionService.getAllReductionActionsForReportPeriod(`organisationWide=true&actionType=${actionType}`)
      .then(rows => setTimelineActions(rows))
      .catch(err => { console.log(err); setTimelineActions([]) })
      .finally(() => setIsTimelineLoading(false))
  }

  useEffect(() => {
    if (view !== 'timeline') return
    loadTimelineActions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, actionType])

  const getAllActionsForOrganisation = () => {
    reductionActionService.getAllReductionActionsForReportPeriodWithPagination(`organisationWide=true&actionType=${actionType}`, inputPage, perPage, orderBy, order)
      .then(response => {
        setIsLoading(false)
        setOrganisationReductionActions(response.rows)
        setTotalRecords(response.count);
      })
      .catch((err) => {
        console.log(err)
        setIsLoading(false)
        setOrganisationReductionActions([])
        setTotalRecords(0)
      })
  }

  useEffect(() => {
    setInputPage(0)
  }, [order, orderBy])

  return (
    <PageOuterContainer>
      <PageHeading
        title={pageTitle}
        hasTabs={false}
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => navigate(addButtonLink)}
        actionButtonText={addButtonText}
        hideShowActionButton={isReportPeriodLocked === true}
        actionButtonVariant='text'
      />
      {emptyInfoText && <InformationBox icon={IconName.InfoOutlined} text={emptyInfoText} marginBottom={2} />}
      <Stack direction='row' justifyContent='flex-end' sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_e: React.MouseEvent<HTMLElement>, next: ReductionActionView | null) => { if (next) setView(next) }}
          aria-label='reduction actions view'
        >
          <ToggleButton value='table' aria-label='Table view' sx={{ width: '50px', pt: 0.5, pb: 0.5 }}>
            <TableRowsOutlined />
          </ToggleButton>
          <ToggleButton value='timeline' aria-label='Timeline view' sx={{ width: '50px', pt: 0.5, pb: 0.5 }}>
            <ViewTimelineOutlined />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {view === 'table' ? (
        <LoadingPage pageReady={!isLoading}>
          {organisationReductionActions &&
            <ReductionActionPlan
              actionType={actionType}
              setOrder={setOrder}
              setOrderBy={setOrderBy}
              reductionActions={organisationReductionActions}
              getAllActionsForOrganisation={getAllActionsForOrganisation}
              inputPage={inputPage}
              setInputPage={setInputPage}
              totalRecords={totalRecords}
              perPage={perPage}
              setPerPage={setPerPage} />}
        </LoadingPage>
      ) : (
        <LoadingPage pageReady={!isTimelineLoading}>
          {timelineActions && (
            <ReductionActionTimeline
              actions={timelineActions}
              actionType={actionType}
              onActionUpdated={loadTimelineActions}
              baseYear={baseYear}
            />
          )}
        </LoadingPage>
      )}
    </PageOuterContainer>
  )
}